import React, {useEffect, useMemo, useState} from 'react';
import Header from "../Header";
import {Grid, Dialog, DialogTitle, DialogActions, DialogContent} from "@mui/material";
import TextInput from "../Inputs/TextInput";
import NumberInput from "../Inputs/NumberInput";
import {Button} from "@material-ui/core";
import useServiceUrls from "../../Utils/UseServiceUrls";
import {closeWindow, getQueryString, handleCartAlerts, onSnippetComplete, hasCartIdentifier, completeSnippetOrChainAlerts, buildAlertResumeRouteUrl, isAlertSnippet} from "../../Helpers/Globals";
import useAcknowledgeAlert from "../../Utils/UseAcknowledgeAlert";
import useAvailableCoupons from "../../Utils/UseAvailableCoupons";
import useCustomerCoupons from "../../Utils/UseCustomerCoupons";
import useApplyCoupon from "../../Utils/UseApplyCoupon";
import useRedeemCoupon from "../../Utils/UseRedeemCoupon";
import LoadingSpinner from "../LoadingSpinner";
import useGetCart from "../../Utils/UseGetCart";
import ClickableRowTable from "../Tables/ClickableRowTable";
import useRemoveCoupon from "../../Utils/UseRemoveCoupon";
import CloseButton from "../PageControls/CloseButton";
import useCoupon from "../../Utils/UseCoupon";

export default function ApplyCoupon(props) {
    const { hideHeader = false, customer, customerUid, onCouponRedeemed } = props;
    const [sessionKey, setSessionKey] = useState('');
    const [loginDomain, setLoginDomain] = useState('');
    const [cartUid, setCartUid] = useState('');
    const [cartExternalId, setCartExternalId] = useState('');
    const [cartSourceExternalId, setCartSourceExternalId] = useState('');
    const [returnUrl, setReturnUrl] = useState('')
    const [alertUid, setAlertUid] = useState('')
    const { serviceUrlsStatus, serviceUrls, serviceUrlsError } = useServiceUrls(loginDomain);
    const { status: cartStatus, cartResponse, error: cartError, refetch } = useGetCart(serviceUrls, sessionKey, cartUid, cartExternalId, cartSourceExternalId);
    
    const hasCart = useMemo(() => hasCartIdentifier(cartUid, cartExternalId, cartSourceExternalId), [cartUid, cartExternalId, cartSourceExternalId]);
    const effectiveCustomerUid = useMemo(() => {
        return customerUid || cartResponse?.Cart?.Customer?.Uid;
    }, [customerUid, cartResponse]);
    
    const {status: availableCouponsStatus, coupons: availableCoupons, error: availableCouponsError} = useAvailableCoupons(serviceUrls, sessionKey, cartUid, cartExternalId, cartSourceExternalId);
    const {status: customerCouponsStatus, coupons: customerCoupons, error: customerCouponsError, refetch: refetchCustomerCoupons} = useCustomerCoupons(serviceUrls, sessionKey, effectiveCustomerUid);
    
    const coupons = useMemo(() => {
        return hasCart ? availableCoupons : customerCoupons;
    }, [hasCart, availableCoupons, customerCoupons]);
    
    const couponsStatus = useMemo(() => {
        return hasCart ? availableCouponsStatus : customerCouponsStatus;
    }, [hasCart, availableCouponsStatus, customerCouponsStatus]);
    
    const couponsError = useMemo(() => {
        return hasCart ? availableCouponsError : customerCouponsError;
    }, [hasCart, availableCouponsError, customerCouponsError]);
    const [searchCode, setSearchCode] = useState('')
    const {status: couponStatus, coupon, error: couponError} = useCoupon(serviceUrls, sessionKey, searchCode); 
    
    const apply = useApplyCoupon()
    const redeem = useRedeemCoupon()
    const remove = useRemoveCoupon()
    const acknowledgeAlert = useAcknowledgeAlert()
    
    const [search, setSearch] = useState('')
    const [quantity, setQuantity] = useState('1')
    const [options, setOptions] = useState([])
    const [showOptions, setShowOptions] = useState(false)
    const [selectedCoupon, setSelectedCoupon] = useState('')
    const [exactMatch, setExactMatch] = useState(false)
    const [showRedeemConfirm, setShowRedeemConfirm] = useState(false)
    const [couponToRedeem, setCouponToRedeem] = useState(null)
    const [showSuccessMessage, setShowSuccessMessage] = useState(false)
    
    const appliedCoupons = useMemo(() => {
        return cartResponse?.LoyaltySummary?.AppliedCoupons ?? []
    }, [cartResponse, cartStatus])
    
    // Automatically show coupon list when there's no cart (only on initial load)
    useEffect(() => {
        if (!hasCart && coupons && coupons.length > 0 && !showOptions) {
            setShowOptions(true)
            setOptions(coupons)
        }
    }, [hasCart, coupons, showOptions])
    
    useEffect(() => {
        setSessionKey(getQueryString(window.location.href, 'bl_sk') ?? getQueryString(window.location.href,'SessionKey') ?? '');
        setLoginDomain(getQueryString(window.location.href, 'bl_LoginDomain') ?? getQueryString(window.location.href,'LoginDomain') ?? '');
        setReturnUrl(getQueryString(window.location.href, 'bl_rurl') ?? getQueryString(window.location.href,'ReturnUrl') ?? '')
        setCartUid(getQueryString(window.location.href, 'bL_CartUid') ?? getQueryString(window.location.href,'CartUid') ?? '');
        setCartExternalId(getQueryString(window.location.href, 'bl_CartExternalId') ?? getQueryString(window.location.href,'cartExternalId') ?? '')
        setCartSourceExternalId(getQueryString(window.location.href, 'bl_CartSourceExternalId') ?? getQueryString(window.location.href,'CartSourceExternalId') ?? '')
        setAlertUid(getQueryString(window.location.href, 'bl_alertUid') ?? getQueryString(window.location.href,'alertUid') ?? '')
    }, [window.location.href])

    const acknowledgeCurrentAlert = async () => {
        if (alertUid && hasCartIdentifier(cartUid, cartExternalId, cartSourceExternalId) && serviceUrls && sessionKey) {
            await acknowledgeAlert.mutateAsync({
                urls: serviceUrls,
                accessKey: sessionKey,
                cartUid,
                cartExternalId,
                cartSourceExternalId,
                alertUid
            })
        }
    }

    const refetchCartForAlerts = async () => {
        const { data } = await refetch()
        return data?.data ?? data
    }

    const finishSnippet = async () => {
        await completeSnippetOrChainAlerts({
            refetchCart: refetchCartForAlerts,
            returnUrl,
            acknowledgeAlert: acknowledgeCurrentAlert,
        })
    }
    
    useEffect(() => {
        if (couponStatus === 'success') {
            if (coupon)
                applyCoupon(coupon);
            else
                setShowOptions(true)
        }
    }, [couponStatus])
    
    useEffect(() => {
        if (apply.status === 'success') {
            const wasExactMatch = exactMatch
            setSearch('')
            setQuantity('1')
            setSelectedCoupon('')
            setExactMatch(false)
            if (hasCart && !hideHeader && !isAlertSnippet()) {
                refetch().then(({ data }) => {
                    const cartResponse = data?.data ?? data
                    const completeIfExactMatch = () => {
                        if (wasExactMatch) {
                            onSnippetComplete(returnUrl)
                        }
                    }
                    const redirected = handleCartAlerts(cartResponse, returnUrl, completeIfExactMatch, { resumeUrl: buildAlertResumeRouteUrl(window.location.href, window.location.href) })
                    if (!redirected && wasExactMatch) {
                        onSnippetComplete(returnUrl)
                    }
                })
            } else if (hasCart && !hideHeader) {
                refetch()
            } else {
                refetch()
                if (!hasCart && wasExactMatch) {
                    onSnippetComplete(returnUrl)
                }
            }
        }
    }, [apply.status])

    useEffect(() => {
        if (redeem.status === 'success') {
            const redeemedCode = couponToRedeem?.Code;
            setSearch('')
            setQuantity('1')
            setSelectedCoupon('')
            setShowRedeemConfirm(false)
            // Notify parent component about redeemed coupon
            if (redeemedCode && onCouponRedeemed) {
                onCouponRedeemed(redeemedCode)
            }
            setCouponToRedeem(null)
            setShowSuccessMessage(true)
            // Show coupon list after successful redemption
            setShowOptions(true)
            // Refetch customer coupons to get updated list after redemption
            if (!hasCart && effectiveCustomerUid) {
                refetchCustomerCoupons()
            }
            if (exactMatch) {
                onSnippetComplete(returnUrl)
                setExactMatch(false)
            }
            // Reset the mutation status to allow future redemptions
            redeem.reset()
        }
    }, [redeem.status, coupons, exactMatch, returnUrl, couponToRedeem, onCouponRedeemed, redeem, hasCart, effectiveCustomerUid, refetchCustomerCoupons])
    
    // Sync options with coupons when coupons data changes (especially after redemption)
    // Only updates when coupons changes, not when search input changes
    // Search filtering is handled by getCouponOptions() when user clicks Find
    useEffect(() => {
        if (showOptions && coupons) {
            // Only update the full list if search is empty
            // If user has a search value, they need to click Find to filter
            if (search === '') {
                setOptions(coupons)
            }
            // Note: We don't update options here if search has a value,
            // to preserve the user's current search/filter state
        }
    }, [coupons, showOptions])
    
    // Auto-close success message after 1 second
    useEffect(() => {
        if (showSuccessMessage) {
            const timer = setTimeout(() => {
                setShowSuccessMessage(false)
            }, 800)
            return () => clearTimeout(timer)
        }
    }, [showSuccessMessage])

    useEffect(() => {
        if (remove.status === 'success') {
            refetch()
            setSearch('')
            setSelectedCoupon('')
        }
    }, [remove.status])
    
    const handleChange = (field, value) => {
        setSearch(value)
    }
    
    const findCoupons = (e) => {
        e.preventDefault()
        const coupon = coupons?.find((c) => c.Code.toLowerCase() === search.toLowerCase())
        if (coupon) {
            applyCoupon(coupon)
            if (selectedCoupon === coupon.Uid)
                setExactMatch(false)
            else
                setExactMatch(true)
        }
        else 
            getCouponOptions()
    }
    
    const getCouponOptions = () => {
        const opts = coupons?.filter((c) => c.Name.toLowerCase().includes(search.toLowerCase()) || c.Code.toLowerCase().includes(search.toLowerCase()))
        if (opts.length === 0)
            setOptions([])
        else
            setOptions(opts)
        
        if (search === '')
            setShowOptions(true)
        else
            setSearchCode(search)
    }
    
    const safeQuantity = () => {
        const q = parseInt(quantity, 10)
        return Number.isNaN(q) || q < 1 ? 1 : q
    }

    const applyCoupon = (coupon) => {
        hideOptions()
        if (!hasCart && effectiveCustomerUid) {
            // Show confirmation dialog for redeem when there's no cart
            setCouponToRedeem(coupon)
            setShowRedeemConfirm(true)
        } else {
            // Apply to cart when cart exists
            apply.mutate({urls: serviceUrls, cartUid, cartExternalId, cartSourceExternalId, uid: coupon.Uid, code: coupon.Code, quantity: safeQuantity(), accessKey: sessionKey})
        }
    }
    
    const confirmRedeem = () => {
        if (couponToRedeem && effectiveCustomerUid) {
            redeem.mutate({
                urls: serviceUrls,
                accessKey: sessionKey,
                customerUid: effectiveCustomerUid,
                uid: couponToRedeem.Uid,
                code: couponToRedeem.Code,
                quantity: safeQuantity()
            })
        }
    }
    
    const cancelRedeem = () => {
        setShowRedeemConfirm(false)
        setCouponToRedeem(null)
    }
    
    const removeCoupon = () => {
        const coupon = cartResponse?.LoyaltySummary?.AppliedCoupons?.find((c) => c.Uid === selectedCoupon)
        const quantity = coupon?.QuantityApplied
        remove.mutate({urls: serviceUrls, cartUid, cartExternalId, cartSourceExternalId, uid: coupon.Uid, code: coupon.Code, quantity: quantity === 1 || quantity === 0 ? null : 1, accessKey: sessionKey})
    }
    
    const hideOptions = () => {
        setShowOptions(false)
        setOptions([])
    }
    
    const onAppliedTableRowClick = (coupon) => {
        if (selectedCoupon === coupon.Uid)
            setSelectedCoupon('')
        else
            setSelectedCoupon(coupon.Uid)
    }
    
    const onSearchTableRowClick = coupon => {
        if (search === coupon.Code) {
            setSearch('')
            setSelectedCoupon('')
        } else {
            setSearch(coupon.Code)
            setSelectedCoupon(coupon.Uid)
        }
    }

    const onSearchTableRowDoubleClick = coupon => {
        applyCoupon(coupon)
    }
    
    
    const couponColumns = useMemo(() => [
        {
            Header: 'Code',
            accessor: 'Code',
        },
        {
            Header: 'Name',
            accessor: 'Name',
        },
        {
            Header: 'Quantity Available',
            accessor: 'QuantityAvailable',
            Cell: ({row: {original}}) => <p className={'table-text'}>{original.QuantityAvailable === null ? '' : `${original.QuantityAvailable} available`}</p>
        }
    ], [])

    const appliedCouponColumns = useMemo(() => [
        {
            Header: 'Code',
            accessor: 'Code',
        },
        {
            Header: 'Name',
            accessor: 'Name',
        },
        {
            Header: 'Quantity',
            accessor: 'QuantityApplied',
            Cell: ({row: {original}}) => original.QuantityApplied === 1 ? <></> : original.QuantityApplied === 0 ? 'Not Valid' : <>{`x${original.QuantityApplied}`}</>
        }
    ], [])
    
    return (
        <div className={'container'}>
            {!hideHeader && <Header title={'Apply Coupon'}/>}
            {/*{cartStatus === 'success' && !cartResponse && <div> <p>Unable to find cart</p><Button onClick={() => onSnippetComplete(returnUrl)} variant='contained' color={'primary'}>Close</Button></div>}*/}
            {coupons &&
                <>
                    <div className={'snippetContainer center'}>
                        <form onSubmit={findCoupons}>
                            <Grid container spacing={2} alignItems={'center'}>
                                <Grid item xs={6} sm={7}>
                                    <TextInput required={false} id={'Coupon-Search'} value={search} label={''} variant={'outlined'} width={'100%'}
                                               handleChange={handleChange}/>
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <NumberInput id={'quantity'} label={'Quantity'} value={quantity} width={'100%'} variant={'outlined'}
                                                 handleChange={(id, value) => setQuantity(value)}/>
                                </Grid>
                                <Grid item xs={3} sm={3}>
                                    <Button type={'submit'} variant='contained' color={'primary'}>Find / Apply</Button>
                                </Grid>
                            </Grid>
                        </form>
                        <br />
                        {!showOptions && 
                            <>
                                <div>
                                    <p className={'bold left'} style={{display: 'inline'}}>Applied Coupons</p>
                                    {selectedCoupon ? <Button className={'right'} variant={'contained'} color={'primary'} onClick={removeCoupon}>Remove</Button> : <></>}
                                </div>
                                <div className={`table-wrapper-div`}>
                                    <ClickableRowTable columns={appliedCouponColumns} data={appliedCoupons} hideHeader={true} onRowClick={onAppliedTableRowClick} selected={selectedCoupon}/>
                                </div>
                            </>
                        }
                        {showOptions &&
                            <>
                                <p className={'bold left'}>Select Coupons to Apply</p>
                                <div className={`table-wrapper-div`}>
                                    {options.length > 0 && 
                                        <ClickableRowTable columns={couponColumns} data={options} hideHeader={true} onRowClick={onSearchTableRowClick} selected={selectedCoupon} onDoubleClick={onSearchTableRowDoubleClick}/>
                                    }
                                    {options.length === 0 && <p>No Coupons Found</p>}
                                </div>
                            </>
                        }
                        {!hideHeader && <CloseButton onClose={() => finishSnippet()} label={showOptions ? 'Cancel' : 'Close'}/>}
                    </div>
                </>
            }
            {(couponsStatus === 'loading' || apply.status === 'loading' || cartStatus === 'loading' || remove.status === 'loading') && <LoadingSpinner />}
            
            <Dialog open={showRedeemConfirm} onClose={redeem.status === 'loading' ? undefined : cancelRedeem}>
                <DialogTitle>Confirm Redemption</DialogTitle>
                <DialogContent>
                    {redeem.status === 'loading' ? (
                        <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                            <LoadingSpinner />
                            <p>Redeeming coupon...</p>
                        </div>
                    ) : (
                        <p>This will mark the coupon as redeemed, are you sure you'd like to redeem?</p>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelRedeem} variant='contained' color={'secondary'} disabled={redeem.status === 'loading'}>Cancel</Button>
                    <Button onClick={confirmRedeem} variant='contained' color={'primary'} disabled={redeem.status === 'loading'}>Proceed</Button>
                </DialogActions>
            </Dialog>
            
            <Dialog open={showSuccessMessage} onClose={() => setShowSuccessMessage(false)}>
                <DialogTitle>Success</DialogTitle>
                <DialogContent>
                    <p>Coupon has been successfully redeemed.</p>
                </DialogContent>
            </Dialog>
        </div>
    )
}